if (!customElements.get('mega-bundle')) {
  class MegaBundle extends HTMLElement {
    constructor() {
      super();

      // ----- Config (from data-*) -----
      this.wrapperVariantId = this.getAttribute('data-wrapper-variant-id') || null;
      this.minProducts = parseInt(this.getAttribute('data-base-slots') || '0', 10) || 0;
      this.maxSlots = parseInt(this.getAttribute('data-slot-total') || '0', 10) || 0;
      this.slotDiscounts = (this.getAttribute('data-slot-discounts') || '')
        .split(',')
        .map((n) => parseInt(n, 10) || 0);
      this.giftProducts = JSON.parse(this.getAttribute('data-gift-products') || '[]');
      this.emptyImage = this.getAttribute('data-empty-image') || null;
      this.emptyText = this.getAttribute('data-empty-text') || '';
      this.emptyTextOptional = this.getAttribute('data-empty-text-optional') || this.emptyText;
      this.texts = {
        min: this.getAttribute('data-text-min') || '',
        more: this.getAttribute('data-text-more') || '',
        max: this.getAttribute('data-text-max') || '',
        popup: this.getAttribute('data-text-popup') || '',
        popupFull: this.getAttribute('data-text-popup-full') || '',
        rabatt: this.getAttribute('data-text-rabatt') || '%discount%% Rabatt',
        sparst: this.getAttribute('data-text-sparst') || 'Du sparst %amount%',
        slotSave: this.getAttribute('data-text-slot-save') || 'Du sparst'
      };

      // ----- State -----
      // Ordered by first-added. Same product keeps a single entry with quantity.
      // Discount is FLAT tiered: the whole bundle gets one rate based on total units.
      this.items = [];
      this.activeCat = 0;
      // null = no sub-tab selected -> show all products of the active category.
      this.activeTab = null;
      this.modalProduct = null;
      this.modalQuantity = 1;

      // ----- Refs -----
      this.cartDrawer = document.querySelector('cart-drawer');
      this.navEl = this.querySelector('[data-nav]');
      this.categoriesEl = this.querySelector('[data-categories]');
      this.categoryButtons = this.querySelectorAll('[data-category-button]');
      this.tabsets = this.querySelectorAll('[data-tabs]');
      this.tabButtons = this.querySelectorAll('[data-tab-button]');
      this.panels = this.querySelectorAll('[data-panel]');
      this.barEl = this.querySelector('[data-bar]');
      this.slotsEl = this.querySelector('[data-slots]');
      this.slotsPrev = this.querySelector('[data-slots-prev]');
      this.slotsNext = this.querySelector('[data-slots-next]');
      this.rabattEl = this.querySelector('[data-rabatt]');
      this.sparstEl = this.querySelector('[data-sparst]');
      this.summeValueEl = this.querySelector('[data-summe-value]');
      this.summeCompareEl = this.querySelector('[data-summe-compare]');
      this.progressTextEl = this.querySelector('[data-progress-text]');
      this.progressFillEl = this.querySelector('[data-progress-fill]');
      this.nextButton = this.querySelector('[data-next]');
      // Add-to-cart label swaps to the "incomplete" text until the minimum is reached.
      this.cartText = this.nextButton ? this.nextButton.textContent.trim() : '';
      this.cartIncompleteText = this.getAttribute('data-text-cart-incomplete') || this.cartText;
      this.popupEl = this.querySelector('[data-popup]');
      this.popupTextEl = this.querySelector('[data-popup-text]');
      this.modalEl = this.querySelector('[data-modal]');
      this.modalMediaEl = this.querySelector('[data-modal-media]');
      this.modalTitleEl = this.querySelector('[data-modal-title]');
      this.modalPriceEl = this.querySelector('[data-modal-price]');
      this.modalDescriptionEl = this.querySelector('[data-modal-description]');
      this.modalQtyEl = this.querySelector('[data-modal-qty]');
      this.modalAddEl = this.querySelector('[data-modal-add]');
      this.modalNutritionEl = this.querySelector('[data-modal-nutrition]');

      // ----- Bind handlers (in constructor, per project rules) -----
      this.onClick = this.handleClick.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onScroll = this.handleScroll.bind(this);
      this.popupTimer = null;
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);

      // Escape schliesst den Produktdetail-Drawer, wie bei jedem Dialog.
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!this.modalEl || this.modalEl.hasAttribute('data-hidden')) return;
        event.stopPropagation();
        this.closeModal();
      });

      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.slotsEl) this.slotsEl.addEventListener('scroll', () => this.updateSlotsNav(), { passive: true });
      if (this.categoriesEl) {
        this.categoriesEl.addEventListener('scroll', () => this.updateCategoryScroll(), { passive: true });
      }
      this.updateBarHeight();
      this.updateStickyTop();
      this.updateStuck();
      this.updateCategoryScroll();
      // Sync sub-tab state: single-tab category -> tab selected; otherwise all-view.
      const initCat = String(this.activeCat);
      const initJi = this.tabCountForCat(initCat) === 1 ? '0' : null;
      this.activeTab = initJi === null ? null : 0;
      this.updateTabState(initCat, initJi);
      this.showPanel(initCat, initJi);
      // The bar can reflow after fonts/images load and change height; re-measure.
      if (this.barEl && 'ResizeObserver' in window) {
        this.barObserver = new ResizeObserver(() => this.updateBarHeight());
        this.barObserver.observe(this.barEl);
      }
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      if (this.popupTimer) clearTimeout(this.popupTimer);
      if (this.barObserver) this.barObserver.disconnect();
      document.documentElement.style.removeProperty('--mega-bar-height');
    }

    handleResize() {
      this.updateBarHeight();
      this.updateStickyTop();
      this.updateStuck();
      this.updateSlotsNav();
      this.updateCategoryScroll();
    }

    handleScroll() {
      this.updateStuck();
    }

    updateStickyTop() {
      const header = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
      const height = header ? Math.round(header.getBoundingClientRect().height - 10) : 0;
      this.style.setProperty('--mega-sticky-top', `${height}px`);
    }

    updateStuck() {
      if (!this.navEl) return;
      const stickyTop = parseFloat(this.style.getPropertyValue('--mega-sticky-top')) || 0;
      // Measure the nav's NATURAL top via a non-sticky element (header band bottom,
      // or the section top) so it is reliable even while the nav is pinned. Icons
      // hide the moment the nav reaches its pinned position.
      const band = this.querySelector('.mega-bundle__header');
      const navNaturalTop = band ? band.getBoundingClientRect().bottom : this.getBoundingClientRect().top;
      this.navEl.toggleAttribute('data-stuck', navNaturalTop <= stickyTop);
    }

    centerCategory(ci) {
      if (!this.categoriesEl) return;
      const btn = this.querySelector(`[data-category-button][data-cat="${ci}"]`);
      if (btn) btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    // Toggle the right-edge fade hint while more categories remain off-screen.
    updateCategoryScroll() {
      const el = this.categoriesEl;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      el.toggleAttribute('data-can-scroll-right', max > 4 && el.scrollLeft < max - 2);
    }

    // Reserve exactly the fixed bar's height below the footer (no hardcoded surplus).
    updateBarHeight() {
      if (!this.barEl) return;
      const height = Math.round(this.barEl.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty('--mega-bar-height', `${height}px`);
      }
    }

    // ===== Slots slider =====
    scrollSlots(direction) {
      if (!this.slotsEl) return;
      const amount = Math.max(160, Math.round(this.slotsEl.clientWidth * 0.7));
      this.slotsEl.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }

    updateSlotsNav() {
      if (!this.slotsEl) return;
      const overflow = this.slotsEl.scrollWidth - this.slotsEl.clientWidth > 4;
      if (this.slotsPrev) {
        this.slotsPrev.toggleAttribute('data-hidden', !overflow);
        this.slotsPrev.toggleAttribute('data-disabled', this.slotsEl.scrollLeft <= 2);
      }
      if (this.slotsNext) {
        this.slotsNext.toggleAttribute('data-hidden', !overflow);
        const atEnd = this.slotsEl.scrollLeft + this.slotsEl.clientWidth >= this.slotsEl.scrollWidth - 2;
        this.slotsNext.toggleAttribute('data-disabled', atEnd);
      }
    }

    // ===== Event delegation =====
    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-quickview]')) {
        this.openModal(target.closest('[data-product]'));
        return;
      }
      if (target.closest('[data-add]')) {
        this.addProduct(this.readCard(target.closest('[data-product]')), 1);
        return;
      }
      if (target.closest('[data-stepper] [data-minus]')) {
        this.changeQuantity(target.closest('[data-product]').dataset.id, -1);
        return;
      }
      if (target.closest('[data-stepper] [data-plus]')) {
        this.changeQuantity(target.closest('[data-product]').dataset.id, 1);
        return;
      }
      if (target.closest('[data-slots-prev]')) {
        if (!target.closest('[data-slots-prev]').hasAttribute('data-disabled')) this.scrollSlots(-1);
        return;
      }
      if (target.closest('[data-slots-next]')) {
        if (!target.closest('[data-slots-next]').hasAttribute('data-disabled')) this.scrollSlots(1);
        return;
      }
      if (target.closest('[data-slot][data-filled]')) {
        this.changeQuantity(target.closest('[data-slot]').dataset.id, -1);
        return;
      }
      if (target.closest('[data-category-button]')) {
        this.setActiveCategory(target.closest('[data-category-button]').dataset.cat);
        return;
      }
      if (target.closest('[data-tab-button]')) {
        const btn = target.closest('[data-tab-button]');
        // Single-tab category: the lone tab stays selected (no deselect).
        // Otherwise clicking the already-active sub-tab deselects it -> show all.
        let next = btn.dataset.target;
        if (this.tabCountForCat(btn.dataset.cat) !== 1) {
          next = btn.hasAttribute('data-active') ? null : btn.dataset.target;
        }
        this.setActiveTab(btn.dataset.cat, next);
        return;
      }
      if (target.closest('[data-next]')) {
        this.handleNext();
        return;
      }
      if (target.closest('[data-back]')) {
        if (window.history.length > 1) window.history.back();
        return;
      }
      if (target.closest('[data-modal-close]') || target.closest('[data-modal-backdrop]')) {
        this.closeModal();
        return;
      }
      if (target.closest('[data-modal-minus]')) {
        this.changeModalQuantity(-1);
        return;
      }
      if (target.closest('[data-modal-plus]')) {
        this.changeModalQuantity(1);
        return;
      }
      if (target.closest('[data-modal-add]')) {
        if (this.modalProduct) this.addProduct(this.modalProduct, this.modalQuantity);
        this.closeModal();
        return;
      }
      if (target.closest('[data-nv-tab]')) {
        this.setNvTab(target.closest('[data-nv-tab]'));
        return;
      }
      if (target.closest('[data-popup-close]')) {
        this.hidePopup();
      }
    }

    // ===== Cards / data =====
    readCard(card) {
      if (!card) return null;
      let images = [];
      try {
        images = JSON.parse(card.dataset.images || '[]');
      } catch (e) {
        images = [];
      }
      if (images.length === 0) {
        const img = card.querySelector('img.mega-bundle__image');
        if (img && img.currentSrc) images = [img.currentSrc];
        else if (img && img.src) images = [img.src];
      }
      return {
        id: card.dataset.id,
        handle: card.dataset.handle || '',
        title: card.dataset.title || '',
        price: parseInt(card.dataset.price || '0', 10) || 0,
        compareAtPrice: parseInt(card.dataset.compareAtPrice || '0', 10) || 0,
        sku: card.dataset.sku || '',
        description: card.dataset.description || '',
        images
      };
    }

    // ===== Selection =====
    getTotalUnits() {
      return this.items.reduce((sum, item) => sum + item.quantity, 0);
    }

    remainingSlots() {
      return Math.max(0, this.maxSlots - this.getTotalUnits());
    }

    // Flat tiered rate for the WHOLE bundle, by total unit count.
    currentRate() {
      const units = this.getTotalUnits();
      if (units <= 0) return 0;
      const index = Math.min(units, this.maxSlots) - 1;
      return this.slotDiscounts[index] || 0;
    }

    addProduct(data, quantity) {
      if (!data || !data.id) return;
      const addQty = Math.min(quantity, this.remainingSlots());
      if (addQty <= 0) {
        const fullMessage = (this.texts.popupFull || this.texts.popup)
          .replace(/%max%/g, String(this.maxSlots))
          .replace(/%min%/g, String(this.maxSlots));
        this.showPopup(fullMessage);
        return;
      }

      const existing = this.items.find((item) => String(item.id) === String(data.id));
      if (existing) {
        existing.quantity += addQty;
      } else {
        this.items.push({ ...data, quantity: addQty });
      }
      this.render();
    }

    changeQuantity(id, delta) {
      const item = this.items.find((i) => String(i.id) === String(id));
      if (!item) {
        if (delta > 0) {
          const card = this.querySelector(`[data-product][data-id="${id}"]`);
          this.addProduct(this.readCard(card), 1);
        }
        return;
      }
      if (delta > 0 && this.remainingSlots() <= 0) return;
      item.quantity += delta;
      if (item.quantity <= 0) {
        this.items = this.items.filter((i) => i !== item);
      }
      this.render();
    }

    removeItem(id) {
      this.items = this.items.filter((i) => String(i.id) !== String(id));
      this.render();
    }

    // ===== Pricing (flat tiered discount) =====
    computeBundle() {
      const rate = this.currentRate();
      const selections = [];
      const prices = [];
      const titles = [];
      const handles = [];
      const skus = [];
      let total = 0;
      let compareTotal = 0;

      this.items.forEach((item) => {
        const finalPrice = Math.round(item.price * (1 - rate / 100));
        for (let q = 0; q < item.quantity; q++) {
          selections.push(`gid://shopify/ProductVariant/${item.id}`);
          prices.push(finalPrice);
          titles.push(item.title);
          handles.push(item.handle || '');
          if (item.sku) skus.push(item.sku);
          total += finalPrice;
          compareTotal += item.price;
        }
      });

      return { rate, selections, prices, titles, handles, skus, total, compareTotal };
    }

    // ===== Rendering =====
    render() {
      const totalUnits = this.getTotalUnits();
      const bundle = this.computeBundle();
      const saved = bundle.compareTotal - bundle.total;

      // Discount rate label
      if (this.rabattEl) {
        if (bundle.rate > 0) {
          this.rabattEl.textContent = this.texts.rabatt.replace(/%discount%/g, String(bundle.rate));
          this.rabattEl.toggleAttribute('data-hidden', false);
        } else {
          this.rabattEl.toggleAttribute('data-hidden', true);
        }
      }

      // Savings label — only once the saving reaches 5 € (prices are in cents).
      // Das Limit steht in Shop-Waehrung, die Preise stehen in der Waehrung des
      // Marktes — ohne den Kurs waeren es in DK 5 kr statt 5 €.
      const savingsLimit = 500 * Number(window.Shopify?.currency?.rate || 1);
      if (this.sparstEl) {
        if (saved >= savingsLimit) {
          this.sparstEl.textContent = this.texts.sparst.replace(/%amount%/g, this.formatMoney(saved));
          this.sparstEl.toggleAttribute('data-hidden', false);
        } else {
          this.sparstEl.toggleAttribute('data-hidden', true);
        }
      }

      // Totals
      if (this.summeValueEl) this.summeValueEl.textContent = totalUnits > 0 ? this.formatMoney(bundle.total) : '';
      if (this.summeCompareEl) {
        if (saved > 0) {
          this.summeCompareEl.textContent = this.formatMoney(bundle.compareTotal);
          this.summeCompareEl.toggleAttribute('data-hidden', false);
        } else {
          this.summeCompareEl.toggleAttribute('data-hidden', true);
        }
      }

      // Progress bar + text
      this.renderProgress(totalUnits);

      if (this.nextButton) {
        const complete = totalUnits >= this.minProducts;
        this.nextButton.setAttribute('aria-disabled', complete ? 'false' : 'true');
        this.nextButton.textContent = complete ? this.cartText : this.cartIncompleteText;
        // Below the minimum the summary is empty/uninformative -> hide it so the
        // add-to-cart button centers vertically in the bar.
        this.toggleAttribute('data-incomplete', !complete);
      }

      this.toggleAttribute('data-full', this.remainingSlots() <= 0);

      this.renderSlots(totalUnits);
      this.renderCards(bundle.rate);
      this.updateSlotsNav();
      this.scrollSlotsToNext();
    }

    renderProgress(totalUnits) {
      // Fill bar by share of total slots used.
      if (this.progressFillEl) {
        const pct = this.maxSlots > 0 ? Math.min(100, (totalUnits / this.maxSlots) * 100) : 0;
        this.progressFillEl.style.width = `${pct}%`;
      }

      if (!this.progressTextEl) return;
      let text;
      if (totalUnits < this.minProducts) {
        text = this.texts.min.replace(/%min%/g, String(this.minProducts));
      } else {
        // Find the next slot index whose discount is higher than the current rate.
        const current = this.currentRate();
        let nextIndex = -1;
        for (let i = totalUnits; i < this.maxSlots; i++) {
          if ((this.slotDiscounts[i] || 0) > current) {
            nextIndex = i;
            break;
          }
        }
        if (nextIndex < 0) {
          const maxDiscount = this.slotDiscounts[this.maxSlots - 1] || current;
          text = this.texts.max.replace(/%discount%/g, String(maxDiscount));
        } else {
          const needed = nextIndex + 1 - totalUnits;
          const nextDiscount = this.slotDiscounts[nextIndex] || 0;
          text = this.texts.more
            .replace(/%n%/g, String(needed))
            .replace(/%discount%/g, String(nextDiscount));
        }
      }
      this.progressTextEl.textContent = text;
    }

    slotPill(index) {
      const d = this.slotDiscounts[index] || 0;
      return d > 0
        ? `<span class="mega-bundle__slot-discount">${d}%</span>`
        : '<span class="mega-bundle__slot-discount">Sorte wählen</span>';
    }

    renderSlots(totalUnits) {
      if (!this.slotsEl) return;
      let html = '';

      this.items.forEach((item) => {
        const slotSrc = item.images && item.images[0]
          ? item.images[0].replace(/[?&]width=\d+/, (m) => m[0] + 'width=100')
          : null;
        const image = slotSrc
          ? `<img class="mega-bundle__slot-image" src="${slotSrc}" alt="${this.escapeHtml(item.title)}" loading="lazy">`
          : '';
        for (let q = 0; q < item.quantity; q++) {
          html += `<div class="mega-bundle__slot" data-slot data-filled data-id="${item.id}">
            <button type="button" class="mega-bundle__slot-remove" data-remove-item aria-label="Remove">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="var(--c-black)"/><path d="M15 9l-6 6M9 9l6 6" stroke="var(--c-white)" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
            ${image}
          </div>`;
        }
      });

      for (let k = totalUnits; k < this.maxSlots; k++) {
        const active = k === totalUnits ? ' data-border' : '';
        const placeholder = this.emptyImage
          ? `<img class="mega-bundle__slot-placeholder" src="${this.emptyImage}" alt="" loading="lazy">`
          : '';
        const optional = k >= this.minProducts ? ' data-optional' : '';
        html += `<div class="mega-bundle__slot" data-slot data-empty${optional}${active}>
          ${placeholder}${this.slotPill(k)}
        </div>`;
      }

      this.slotsEl.innerHTML = html;
    }

    renderCards(rate) {
      this.querySelectorAll('[data-product]').forEach((card) => {
        const item = this.items.find((i) => String(i.id) === String(card.dataset.id));
        const qty = item ? item.quantity : 0;
        const price = parseInt(card.dataset.price || '0', 10) || 0;
        const compareAt = parseInt(card.dataset.compareAtPrice || '0', 10) || 0;
        // Strikethrough = the product's original (pre-discount) price. If the
        // product is already discounted in Shopify it carries a compare_at_price
        // above its price; show that, not the already-reduced price.
        const original = compareAt > price ? compareAt : price;

        // Pricing display (flat bundle rate applies to every product).
        const priceEl = card.querySelector('[data-card-price]');
        const compareEl = card.querySelector('[data-card-compare]');
        const discountEl = card.querySelector('[data-card-discount]');
        if (rate > 0) {
          const discounted = Math.round(price * (1 - rate / 100));
          if (priceEl) priceEl.textContent = this.formatMoney(discounted);
          if (compareEl) {
            compareEl.textContent = this.formatMoney(original);
            compareEl.toggleAttribute('data-hidden', false);
          }
          if (discountEl) {
            discountEl.textContent = `-${rate}%`;
            discountEl.toggleAttribute('data-hidden', false);
          }
        } else {
          // No bundle discount -> show plain price, no strikethrough.
          if (priceEl) priceEl.textContent = this.formatMoney(price);
          if (compareEl) compareEl.toggleAttribute('data-hidden', true);
          if (discountEl) discountEl.toggleAttribute('data-hidden', true);
        }

        // Add button vs stepper
        const add = card.querySelector('[data-add]');
        const stepper = card.querySelector('[data-stepper]');
        if (qty > 0) {
          if (add) add.toggleAttribute('data-hidden', true);
          if (stepper) {
            stepper.toggleAttribute('data-hidden', false);
            const qtyEl = stepper.querySelector('[data-qty]');
            if (qtyEl) qtyEl.textContent = String(qty);
            const plus = stepper.querySelector('[data-plus]');
            if (plus) plus.toggleAttribute('data-disabled', this.remainingSlots() <= 0);
          }
        } else {
          if (add) {
            add.toggleAttribute('data-hidden', false);
            add.toggleAttribute('data-disabled', this.remainingSlots() <= 0);
          }
          if (stepper) stepper.toggleAttribute('data-hidden', true);
        }
      });
    }

    scrollSlotsToNext() {
      if (!this.slotsEl) return;
      requestAnimationFrame(() => {
        const target = this.slotsEl.querySelector('[data-slot][data-border]') || this.slotsEl.querySelector('[data-slot][data-empty]');
        if (!target) return;
        const containerRect = this.slotsEl.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const targetCenter = this.slotsEl.scrollLeft + (targetRect.left - containerRect.left) + target.offsetWidth / 2;
        const containerWidth = this.slotsEl.clientWidth;
        const maxScroll = this.slotsEl.scrollWidth - containerWidth;
        const scrollPosition = Math.max(0, Math.min(targetCenter - containerWidth / 2, maxScroll));
        this.slotsEl.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        this.updateSlotsNav();
      });
    }

    // ===== Navigation (categories -> sub-tabs -> panels) =====
    setActiveCategory(cat) {
      const ci = String(cat);
      this.activeCat = parseInt(ci, 10) || 0;
      // Single sub-tab category: auto-select the only tab (can't be deselected).
      // Otherwise open with no sub-tab selected -> all its products are shown.
      const ji = this.tabCountForCat(ci) === 1 ? '0' : null;
      this.activeTab = ji === null ? null : 0;

      this.categoryButtons.forEach((btn) => {
        btn.toggleAttribute('data-active', btn.dataset.cat === ci);
      });
      this.tabsets.forEach((set) => {
        set.toggleAttribute('data-active', set.dataset.cat === ci);
      });
      this.updateTabState(ci, ji);
      this.showPanel(ci, ji);
      this.centerCategory(ci);
    }

    setActiveTab(cat, target) {
      const ci = String(cat);
      // target == null -> deselect: show all products of the category.
      const ji = target == null ? null : String(target);
      this.activeTab = ji === null ? null : parseInt(ji, 10) || 0;
      this.updateTabState(ci, ji);
      this.showPanel(ci, ji);
    }

    updateTabState(ci, ji) {
      this.tabButtons.forEach((btn) => {
        if (btn.dataset.cat !== ci) return;
        btn.toggleAttribute('data-active', ji !== null && btn.dataset.target === ji);
      });
    }

    // Number of sub-tabs in a category. A category with exactly one tab keeps that
    // tab permanently selected (no all-view, no deselect).
    tabCountForCat(ci) {
      let count = 0;
      this.tabButtons.forEach((btn) => {
        if (btn.dataset.cat === ci) count += 1;
      });
      return count;
    }

    showPanel(ci, ji) {
      // All-view (no sub-tab): merge the category's panels into one continuous grid.
      this.toggleAttribute('data-all', ji === null);
      this.panels.forEach((panel) => {
        const show = panel.dataset.cat === ci && (ji === null || panel.dataset.index === ji);
        panel.toggleAttribute('data-active', show);
      });
      this.dedupeAllView(ci, ji === null);
    }

    // In the all-view several sub-tab panels are shown at once, so a product that
    // belongs to multiple sub-tabs (e.g. both High Protein and Glutenfrei) would
    // render once per panel. Hide the repeats so each product shows only once.
    // The product still appears under each sub-tab when that tab is selected on
    // its own (then only one panel is active, so there's nothing to dedupe).
    dedupeAllView(ci, isAll) {
      // Reset any previous dedupe state first.
      this.querySelectorAll('[data-product][data-dupe-hidden]').forEach((card) => {
        card.removeAttribute('data-dupe-hidden');
      });
      if (!isAll) return;
      const seen = new Set();
      this.panels.forEach((panel) => {
        if (panel.dataset.cat !== ci || !panel.hasAttribute('data-active')) return;
        panel.querySelectorAll('[data-product]').forEach((card) => {
          const key = card.dataset.id || card.dataset.handle;
          if (!key) return;
          if (seen.has(key)) {
            card.setAttribute('data-dupe-hidden', '');
          } else {
            seen.add(key);
          }
        });
      });
    }

    // ===== Modal =====
    openModal(card) {
      const data = this.readCard(card);
      if (!data) return;
      this.modalProduct = data;
      this.modalQuantity = 1;

      if (this.modalTitleEl) this.modalTitleEl.textContent = data.title;
      if (this.modalPriceEl) this.modalPriceEl.textContent = this.formatMoney(data.price);
      if (this.modalDescriptionEl) this.modalDescriptionEl.textContent = data.description;
      if (this.modalQtyEl) this.modalQtyEl.textContent = '1';
      this.buildModalSlider(data.images);
      this.injectNutrition(card);

      if (this.modalEl) this.modalEl.toggleAttribute('data-hidden', false);
      document.body.style.overflow = 'hidden';

      // Ohne das oeffnet sich der Drawer, der Tastaturfokus bleibt aber auf der
      // Karte dahinter stehen — es sieht dann aus, als passiere nichts.
      const trigger = document.activeElement;
      this.returnFocusTo = trigger && trigger !== document.body ? trigger : null;
      this.focusModalWhenVisible();
    }

    // Der Drawer ist bis zur Einblend-Animation visibility:hidden und kann so
    // keinen Fokus annehmen — kurz nachfassen, bis er sichtbar ist.
    focusModalWhenVisible(attempt = 0) {
      if (!this.modalEl || this.modalEl.hasAttribute('data-hidden') || attempt > 12) return;

      if (getComputedStyle(this.modalEl).visibility === 'hidden') {
        setTimeout(() => this.focusModalWhenVisible(attempt + 1), 40);
        return;
      }

      const target = this.modalEl.querySelector('[data-modal-close], button:not([disabled])');
      target?.focus();
    }

    closeModal() {
      if (this.modalEl) this.modalEl.toggleAttribute('data-hidden', true);
      this.modalProduct = null;
      document.body.style.overflow = '';

      if (this.returnFocusTo && document.contains(this.returnFocusTo)) {
        this.returnFocusTo.focus();
      }
      this.returnFocusTo = null;
      // Clear content only after the slide-out transition so the drawer
      // doesn't appear empty while animating away.
      setTimeout(() => {
        if (this.modalEl && !this.modalEl.hasAttribute('data-hidden')) return;
        if (this.modalMediaEl) this.modalMediaEl.innerHTML = '';
        if (this.modalNutritionEl) this.modalNutritionEl.innerHTML = '';
      }, 300);
    }

    // ===== Nutritional values (cloned from the card's <template>) =====
    injectNutrition(card) {
      if (!this.modalNutritionEl) return;
      this.modalNutritionEl.innerHTML = '';
      const tpl = card ? card.querySelector('template[data-nutrition]') : null;
      if (!tpl || !tpl.content) return;
      this.modalNutritionEl.appendChild(tpl.content.cloneNode(true));
      this.highlightHashtags(this.modalNutritionEl);
    }

    // Underline #words inside the ingredients text.
    highlightHashtags(root) {
      root.querySelectorAll('[data-nv-ingredients]').forEach((el) => {
        el.innerHTML = el.innerHTML.replace(/#(\w+)/g, '<span class="bundle-nutritional-values__underline">$1</span>');
      });
    }

    setNvTab(tab) {
      const nutrition = tab.closest('.bundle-nutritional-values');
      if (!nutrition) return;
      const index = tab.dataset.nvTab;
      nutrition.querySelectorAll('[data-nv-tab]').forEach((el) => {
        el.toggleAttribute('data-active', el.dataset.nvTab === index);
      });
      nutrition.querySelectorAll('[data-nv-content]').forEach((el) => {
        el.toggleAttribute('data-active', el.dataset.nvContent === index);
      });
    }

    changeModalQuantity(delta) {
      this.modalQuantity = Math.max(1, this.modalQuantity + delta);
      if (this.modalQtyEl) this.modalQtyEl.textContent = String(this.modalQuantity);
    }

    buildModalSlider(images) {
      if (!this.modalMediaEl) return;
      if (!images || images.length === 0) {
        this.modalMediaEl.innerHTML = '';
        return;
      }

      // Single image — no slider chrome (arrows / thumbnails).
      if (images.length === 1) {
        this.modalMediaEl.innerHTML = `<div class="mega-bundle__slide mega-bundle__slide-single"><img src="${images[0]}" alt="" loading="lazy" draggable="false"></div>`;
        return;
      }

      const slides = images
        .map((src) => `<div class="mega-bundle__slide"><img src="${src}" alt="" loading="lazy" draggable="false"></div>`)
        .join('');
      const dots = images
        .map((src, i) => `<button type="button" class="mega-bundle__slider-dot" data-dot ${i === 0 ? 'data-active' : ''} aria-label="${i + 1}"></button>`)
        .join('');

      this.modalMediaEl.innerHTML = `
        <simple-slider class="mega-bundle__slider">
          <div class="mega-bundle__slider-list" data-list>${slides}</div>
          <div class="mega-bundle__slider-controls">
            <button type="button" class="mega-bundle__slider-nav" data-left aria-label="Previous">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="16" viewBox="0 0 28 16" fill="none"><path d="M27 8H1m0 0l7-7M1 8l7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div class="mega-bundle__slider-dots">${dots}</div>
            <button type="button" class="mega-bundle__slider-nav" data-right aria-label="Next">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="16" viewBox="0 0 28 16" fill="none"><path d="M1 8h26m0 0l-7-7m7 7l-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </simple-slider>`;
    }

    // ===== Cart =====
    handleNext() {
      if (this.getTotalUnits() < this.minProducts) {
        this.showPopup(this.texts.popup.replace(/%min%/g, String(this.minProducts)));
        return;
      }
      this.addToDrawer();
    }

    showPopup(message) {
      if (!this.popupEl) return;
      if (this.popupTextEl) this.popupTextEl.textContent = message;
      this.popupEl.toggleAttribute('data-hidden', false);
      if (this.popupTimer) clearTimeout(this.popupTimer);
      this.popupTimer = setTimeout(() => this.hidePopup(), 4000);
    }

    hidePopup() {
      if (this.popupEl) this.popupEl.toggleAttribute('data-hidden', true);
    }

    buildLineItem() {
      const bundle = this.computeBundle();
      if (bundle.selections.length === 0) return null;

      let selections = bundle.selections;
      let prices = bundle.prices;
      // `_Varianten` carries product HANDLES (one per unit); the cart drawer
      // resolves each handle to its title + image via `all_products`.
      let handles = [...bundle.handles];
      let skus = [...bundle.skus];

      // Free gift products expand first (price 0), matching configurator behaviour.
      if (this.giftProducts.length > 0) {
        const giftGids = this.giftProducts.map((g) => `gid://shopify/ProductVariant/${g.variantId}`);
        selections = [...giftGids, ...selections];
        prices = [...this.giftProducts.map(() => 0), ...prices];
        handles = [...this.giftProducts.map((g) => g.handle || ''), ...handles];
        skus = [...this.giftProducts.map((g) => g.sku).filter(Boolean), ...skus];
      }

      const properties = {
        _Varianten: handles.join('; '),
        _Artikelnummern: skus.join(','),
        _cb_selections: selections.join(','),
        _cb_prices: prices.join(',')
      };
      if (bundle.compareTotal > bundle.total) {
        properties._cb_compare_total = String(bundle.compareTotal);
      }

      return { id: this.wrapperVariantId, quantity: 1, properties };
    }

    async addToDrawer() {
      const lineItem = this.buildLineItem();
      if (!lineItem || !lineItem.id) return;

      try {
        // Ask the Section Rendering API for the cart drawer AND the header cart
        // icon bubble in the same request, so both update without a page reload.
        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [lineItem],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();
        if (data && data.sections) {
          this.renderSections(data.sections);
        } else {
          await this.refreshDrawer();
        }
        if (this.cartDrawer && typeof this.cartDrawer.open === 'function') {
          this.cartDrawer.open();
        }
        this.items = [];
        this.render();
      } catch (error) {
        console.error('Mega bundle add to cart failed:', error);
      }
    }

    renderSections(sections) {
      const drawerHtml = sections['cart-drawer'];
      if (drawerHtml) {
        const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
        const newInner = doc.querySelector('.drawer__inner');
        const currentInner = document.querySelector('.drawer__inner');
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.classList.remove('is-empty');
        if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;
      }

      const bubbleHtml = sections['cart-icon-bubble'];
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubbleEl) {
        const section = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
        if (section) bubbleEl.innerHTML = section.innerHTML;
      }
    }

    async refreshDrawer() {
      const res = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newInner = doc.querySelector('.drawer__inner');
      const currentInner = document.querySelector('.drawer__inner');
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.remove('is-empty');
      if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;
    }

    // ===== Utils =====
    formatMoney(cents) {
      // Waehrung kommt aus snippets/money-format.liquid, damit in Maerkten mit
      // anderer Waehrung nicht der umgerechnete Betrag mit Eurozeichen dasteht.
      return window.brekkyFormatMoney
        ? window.brekkyFormatMoney(cents)
        : (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }

  customElements.define('mega-bundle', MegaBundle);
}
